import { useState, useEffect } from 'react';
import type { MapRef } from 'react-map-gl';

import type { DesignCounts } from './DonutClusterMarker';

export interface ClusterData {
  id: number;
  longitude: number;
  latitude: number;
  designCounts: DesignCounts;
  totalCount: number;
}

export function useClusterData(
  mapRef: MapRef | null,
  sourceId: string = 'data-studies',
): ClusterData[] {
  const [clusters, setClusters] = useState<ClusterData[]>([]);

  useEffect(() => {
    if (!mapRef) return;

    const map = mapRef.getMap();
    let updateTimeout: NodeJS.Timeout | null = null;

    const updateClusters = () => {
      const source = map.getSource(sourceId);

      if (!source || source.type !== 'geojson') {
        return;
      }

      if (!map.isSourceLoaded(sourceId)) {
        return;
      }

      const bounds = map.getBounds();

      // Query all features from the source
      const features = map.querySourceFeatures(sourceId, {
        sourceLayer: undefined,
      });

      // Filter to only cluster features (exclude individual points)
      // Also filter to current viewport
      const clusterFeatures = features.filter(feature => {
        if (
          !feature.properties ||
          feature.properties.cluster !== true ||
          feature.geometry.type !== 'Point'
        ) {
          return false;
        }

        // Check if cluster is within current viewport bounds
        const [lng, lat] = feature.geometry.coordinates;
        const inBounds = bounds.contains([lng, lat]);

        return inBounds;
      });

      // Extract unique clusters (may have duplicates from different tiles)
      const uniqueClusters = new Map<number, ClusterData>();

      for (const feature of clusterFeatures) {
        if (!feature.properties || feature.geometry.type !== 'Point') continue;

        const cluster_id = feature.properties.cluster_id as number;
        const point_count = feature.properties.point_count as number;
        const [longitude, latitude] = feature.geometry.coordinates;

        // Skip if we've already processed this cluster
        if (uniqueClusters.has(cluster_id)) {
          continue;
        }

        // Extract design counts from cluster properties
        const designCounts: DesignCounts = {
          'Clinical trial':
            (feature.properties.clinicalTrialCount as number) ?? 0,
          'Implementation study':
            (feature.properties.implementationStudyCount as number) ?? 0,
          'Regional programme':
            (feature.properties.regionalProgrammeCount as number) ?? 0,
          'National programme':
            (feature.properties.nationalProgrammeCount as number) ?? 0,
        };

        uniqueClusters.set(cluster_id, {
          id: cluster_id,
          longitude,
          latitude,
          designCounts,
          totalCount: point_count,
        });
      }

      const clusterArray = [...uniqueClusters.values()];

      setClusters(clusterArray);
    };

    // update function to allow source to finish reclustering
    const debouncedUpdate = () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = setTimeout(() => {
        updateClusters();
      }, 100); // delay to let source recalculate clusters
    };

    // Update clusters on initial load
    updateClusters();

    // Update clusters when map moves or zooms (debounced)
    map.on('moveend', debouncedUpdate);
    map.on('zoomend', debouncedUpdate);

    // Also listen to sourcedata events for immediate updates when data changes
    map.on('sourcedata', (event: any) => {
      if (event.sourceId === sourceId && event.isSourceLoaded) {
        debouncedUpdate();
      }
    });

    // Cleanup listeners
    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      map.off('moveend', debouncedUpdate);
      map.off('zoomend', debouncedUpdate);
      map.off('sourcedata', debouncedUpdate);
    };
  }, [mapRef, sourceId]);

  return clusters;
}
